import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { UserAddress, UserAddressPayload } from "./controller";
import { maskCep } from "../../../../utils/masks";
import { State } from "../../../data-types";
import { AnsweringClient } from "../../../../clients/answeringClient";
import { CEP } from "../../../controller";

interface AddressFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: UserAddressPayload, id?: number) => void;
  address?: UserAddress | null; // se presente, estamos editando
  isSubmitting?: boolean;
}

export const AddressFormModal: React.FC<AddressFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  address,
  isSubmitting = false,
}) => {
  const isEditing = Boolean(address);

  const answeringClient = useMemo(() => new AnsweringClient(), []);
  const [form, setForm] = useState<UserAddressPayload>({
    postalCode: "",
    street: "",
    number: "",
    completion: "",
    neighborhood: "",
    city: "",
    state: State.SP,
    label: "",
    makeDefault: false,
  });
  const [isFetchingCep, setIsFetchingCep] = useState(false);
  const [cepError, setCepError] = useState<string | undefined>();
  const [lastFetchedCep, setLastFetchedCep] = useState<string | undefined>();
  const [providedByCep, setProvidedByCep] = useState({
    street: false,
    neighborhood: false,
    city: false,
    state: false,
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  const requiredFields: Array<keyof UserAddressPayload> = [
    "postalCode",
    "street",
    "number",
    "neighborhood",
    "city",
    "state",
  ];

  useEffect(() => {
    if (address) {
      const sanitizedPostalCode = address.postalCode.replace(/\D/g, "");
      setForm({
        postalCode: sanitizedPostalCode,
        street: address.street,
        number: address.number,
        completion: address.completion ?? "",
        neighborhood: address.neighborhood,
        city: address.city,
        state: address.state,
        label: address.label ?? "",
        makeDefault: address.isDefault,
      });
      setLastFetchedCep(sanitizedPostalCode.length === 8 ? sanitizedPostalCode : undefined);
      setCepError(undefined);
      setProvidedByCep({
        street: false,
        neighborhood: false,
        city: false,
        state: false,
      });
      setFieldErrors({});
    } else {
      setForm({
        postalCode: "",
        street: "",
        number: "",
        completion: "",
        neighborhood: "",
        city: "",
        state: State.SP,
        label: "",
        makeDefault: false,
      });
      setLastFetchedCep(undefined);
      setCepError(undefined);
      setProvidedByCep({
        street: false,
        neighborhood: false,
        city: false,
        state: false,
      });
      setFieldErrors({});
    }
  }, [address, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "postalCode") {
      const digitsOnly = value.replace(/\D/g, "");
      setForm((prev) => ({ ...prev, postalCode: digitsOnly.slice(0, 8) }));
      if (digitsOnly.length < 8) {
        setCepError(undefined);
        setLastFetchedCep(undefined);
      }
      setFieldErrors((prev) => ({ ...prev, postalCode: undefined }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "street" || name === "neighborhood" || name === "city" || name === "state") {
      setProvidedByCep((prev) => ({ ...prev, [name]: false }));
    }
    if (requiredFields.includes(name as keyof UserAddressPayload)) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = () => {
    const normalizedPostalCode = form.postalCode.replace(/\D/g, "");
    const updatedErrors: Record<string, string | undefined> = { ...fieldErrors };
    let hasError = false;

    if (normalizedPostalCode.length !== 8) {
      updatedErrors.postalCode = "Informe um CEP válido";
      setCepError("Informe um CEP válido com 8 dígitos.");
      hasError = true;
    } else {
      updatedErrors.postalCode = undefined;
    }

    ["street", "number", "neighborhood", "city", "state"].forEach((fieldName) => {
      const value = (form as Record<string, any>)[fieldName];
      if (!value || String(value).trim().length === 0) {
        updatedErrors[fieldName] = "Campo obrigatório";
        hasError = true;
      } else {
        updatedErrors[fieldName] = undefined;
      }
    });

    setFieldErrors(updatedErrors);

    if (hasError) {
      return;
    }

    setCepError(undefined);

    onSubmit(
      {
        ...form,
        postalCode: normalizedPostalCode,
      },
      address?.id
    );
  };

  useEffect(() => {
    const digitsOnly = form.postalCode.replace(/\D/g, "");
    if (digitsOnly.length !== 8 || digitsOnly === lastFetchedCep) {
      return;
    }

    let isCancelled = false;
    const token = localStorage.getItem("AccessToken");
    if (!token) {
      return;
    }

    const fetchCepData = async () => {
      setIsFetchingCep(true);
      setCepError(undefined);
      try {
        const response = await answeringClient.getAddressData(token, digitsOnly);
        if (isCancelled) {
          return;
        }
        const data = response.data as CEP;
        setForm((prev) => ({
          ...prev,
          street: data.street || prev.street,
          neighborhood: data.neighborhood || prev.neighborhood,
          city: data.city || prev.city,
          state: data.state || prev.state,
        }));
        setProvidedByCep({
          street: Boolean(data.street),
          neighborhood: Boolean(data.neighborhood),
          city: Boolean(data.city),
          state: Boolean(data.state),
        });
        setFieldErrors((prev) => ({
          ...prev,
          street: data.street ? undefined : prev.street,
          neighborhood: data.neighborhood ? undefined : prev.neighborhood,
          city: data.city ? undefined : prev.city,
          state: data.state ? undefined : prev.state,
        }));
        setLastFetchedCep(digitsOnly);
      } catch (error) {
        if (!isCancelled) {
          setCepError("Não foi possível localizar o CEP informado.");
          setProvidedByCep({
            street: false,
            neighborhood: false,
            city: false,
            state: false,
          });
        }
      } finally {
        if (!isCancelled) {
          setIsFetchingCep(false);
        }
      }
    };

    fetchCepData();

    return () => {
      isCancelled = true;
    };
  }, [form.postalCode, answeringClient, lastFetchedCep]);

  const postalHelperText = cepError || fieldErrors.postalCode;
  const postalHasError = Boolean(postalHelperText);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEditing ? "Editar Endereço" : "Novo Endereço"}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="CEP"
              name="postalCode"
              value={maskCep(form.postalCode)}
              onChange={handleChange}
              fullWidth
              required
              inputProps={{ maxLength: 9 }}
              error={postalHasError}
              helperText={postalHelperText}
              InputProps={{
                endAdornment: isFetchingCep ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              label="Rótulo do endereço (opcional)"
              name="label"
              value={form.label}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Estado"
              name="state"
              value={form.state}
              onChange={handleChange}
              fullWidth
              required
              disabled={providedByCep.state}
              error={Boolean(fieldErrors.state)}
              helperText={fieldErrors.state}
            />
          </Grid>
          <Grid item xs={12} sm={9}>
            <TextField
              label="Cidade"
              name="city"
              value={form.city}
              onChange={handleChange}
              fullWidth
              required
              disabled={providedByCep.city}
              error={Boolean(fieldErrors.city)}
              helperText={fieldErrors.city}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Bairro"
              name="neighborhood"
              value={form.neighborhood}
              onChange={handleChange}
              fullWidth
              required
              disabled={providedByCep.neighborhood}
              error={Boolean(fieldErrors.neighborhood)}
              helperText={fieldErrors.neighborhood}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              label="Rua / Avenida"
              name="street"
              value={form.street}
              onChange={handleChange}
              fullWidth
              required
              disabled={providedByCep.street}
              error={Boolean(fieldErrors.street)}
              helperText={fieldErrors.street}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Número"
              name="number"
              value={form.number}
              onChange={handleChange}
              fullWidth
              required
              error={Boolean(fieldErrors.number)}
              helperText={fieldErrors.number}
            />
          </Grid>
          <Grid item xs={12} >
            <TextField
              label="Complemento"
              name="completion"
              value={form.completion}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="makeDefault"
                  checked={Boolean(form.makeDefault)}
                  onChange={handleCheckboxChange}
                />
              }
              label="Definir como endereço padrão"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
          {isEditing ? "Salvar Alterações" : "Cadastrar"}
        </Button>
        <Button onClick={onClose} color="error" variant="outlined" disabled={isSubmitting}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddressFormModal;
